<template>
  <div :class="$style.minimizedListingCard">
    <div :class="$style.container">
      <div :class="$style.image">
        <img v-if="imageUrl" :src="imageUrl" alt="" />
        <HgImagePlaceholder v-else :class="$style.placeholder" />
      </div>
      <div :class="$style.content">
        <div>
          <span :class="$style.title">{{ $t('resultlist.hideListing.title') }}</span>
        </div>
        <span v-if="description">{{ description }}</span>
      </div>
      <div :class="$style.action">
        <HgButton
          theme="secondary-bordered"
          iconPosition="left"
          icon="Eye"
          data-test="unhide-listing-button"
          @click="showListing"
        >
          {{ $t('resultlist.hideListing.actionUnhide') }}
        </HgButton>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { HgButton, HgImagePlaceholder } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

export interface Props {
  numberOfRooms?: number;
  price: string; // price already comes formatted from ListItem component
  livingSpace?: number;
  imageUrl: string; // this could be empty string if no image is available
}

const props = withDefaults(defineProps<Props>(), {
  numberOfRooms: undefined,
  livingSpace: undefined,
});

const emit = defineEmits<{
  (e: 'showListing'): void;
}>();

const description = computed<string>(() => {
  const rooms = props.numberOfRooms
    ? `${props.numberOfRooms} ${t('$domus.listingCard.rooms', props.numberOfRooms)}`
    : undefined;
  const livingSpace = props.livingSpace ? `${props.livingSpace} m²` : undefined;

  const orderedValues = isPlatformIs24()
    ? [rooms, livingSpace, props.price]
    : [props.price, rooms, livingSpace];

  return orderedValues.filter(Boolean).join(', ');
});

const showListing = (): void => {
  // Handle show listing action and tracking here
  emit('showListing');
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.minimizedListingCard {
  filter: grayscale(1);

  .container {
    display: flex;
    flex-direction: column;
    gap: $space-100;
    padding: $space-100;
    border-radius: $space-50;
    outline: none;
    box-shadow: $elevation-minimal;

    @media (min-width: $viewport-md-min) {
      flex-direction: row;
      padding: 0;
    }

    .image {
      display: none;

      @media (min-width: $viewport-md-min) {
        display: contents;
        flex: 1;

        img,
        .placeholder {
          width: 261px;
          height: 100px;
          object-fit: cover;
          border-top-left-radius: $space-50;
          border-bottom-left-radius: $space-50;
        }
      }
    }

    .content {
      flex: 2;

      @media (min-width: $viewport-md-min) {
        display: block;
        padding: 1.25rem $space-100;
        margin: auto;
      }

      .title {
        @include body-1-semi-bold;
      }
    }

    .action {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: center;

      @media (min-width: $viewport-md-min) {
        justify-content: right;
        padding-right: $space-150;
      }
    }
  }
}
</style>
